import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";

/**
 * Every responsive decision no Mantine prop can express, in one place.
 *
 * Width-only `display: none` is not here -- that is `visibleFrom`/`hiddenFrom`, which read the
 * same `theme.breakpoints`. What is left keys off viewport height or pointer type, or sets a
 * property Mantine has no style prop for.
 *
 * `getInitialValueInEffect: false` everywhere: the default seeds the fallback and corrects in an
 * effect, which paints one frame of the desktop layout on a phone.
 */
export interface Layout {
  /** The phone layout: narrow OR short. */
  phone: boolean;
  /** Short, and wide enough for a 300px player column beside a list. */
  landscape: boolean;
  /** The bare height arm. The canvas is compact whichever way round the window is, so
   *  `Timeline.tsx` wants this and not `phone`: a tall narrow window is `phone` but not short. */
  short: boolean;
  /** Chat drops below the player; on review the inspector drops under both columns. */
  twoColumn: boolean;
  /** A finger, not a mouse: 36px minimum tap targets, no tap highlight. */
  coarse: boolean;
}

export function useLayout(): Layout {
  const { breakpoints } = useMantineTheme();
  const opts = { getInitialValueInEffect: false } as const;
  const narrow = useMediaQuery(`(max-width: ${breakpoints.sm})`, false, opts);
  const short = useMediaQuery(`(max-height: ${breakpoints.xs})`, false, opts);
  const wideEnough = useMediaQuery(`(min-width: ${breakpoints.xs})`, true, opts);
  const twoColumn = useMediaQuery(`(max-width: ${breakpoints.lg})`, false, opts);
  const coarse = useMediaQuery("(pointer: coarse)", false, opts);
  return { phone: narrow || short, landscape: short && wideEnough, short, twoColumn, coarse };
}
